import { GrievanceFormState, RootState } from '@/store/types';
import axios from '@/utils/axios';
import { cleanPayload } from '@/utils/payload';
import { clearErrors, createGrievanceValidationSchema, validateSchema } from '@/utils/validation';
import _ from 'lodash';
import * as NProgress from 'nprogress';
import { ActionTree, GetterTree, Module, MutationTree } from 'vuex';

type CreateGrievancePayload = {
    recaptchaVerifyId?: string;
    description: { value: string };
    type?: { value: number | null };
    parentCategory?: { value: number | null };
    category?: { value: number | null };
    anonymous: { value: boolean };
    title?: { value: number | null };
    firstName?: { value: string };
    lastName?: { value: string };
    notification: { value: boolean };
    email: { value: string };
    phone?: { value: string };
    addressLine1?: { value: string };
    addressLine2?: { value: string };
    neighborhood?: { value: string };
    sublocality?: { value: string };
    city?: { value: string };
    state?: { value: string };
    country?: { value: string };
    postalCode?: { value: string };
    sublocality2?: { value: string };
    arealevel2?: { value: string };
    ageGroup?: { value: number | null };
    ethnicGroup?: { value: number | null };
    department?: { value: number | null };
    customTextarea1Toggle?: { value: boolean };
    customTextarea1?: { value: string | null };
    customTextarea2?: { value: string | null };
    firstTimeUsed: { value: boolean };
    referer?: { value: number | null };
    sysDocuments?: { value: string[] };
    customSingle1?: { value: number | null };
    customSingle2?: { value: number | null };
    projectIdMap?: { value: number | null };
    customText1?: { value: string | null };
    customText2?: { value: string | null };
    eventDate?: { value: Date | null };
};

export const state: GrievanceFormState = {
    type: null,
    parentCategory: null,
    category: null,
    description: '',
    documents: [],
    anonymous: false,
    title: null,
    firstName: '',
    lastName: '',
    notification: false,
    email: '',
    phone: '',
    addressLine1: '',
    addressLine2: '',
    neighborhood: '',
    sublocality: '',
    city: '',
    state: '',
    country: '',
    postalCode: '',
    sublocality2: '',
    arealevel2: '',
    ageGroup: null,
    ethnicGroup: null,
    firstTimeUsed: true,
    referer: null,
    acceptTerms: false,
    department: null,
    customTextarea1Toggle: false,
    customTextarea1: '',
    customTextarea2: '',
    recaptchaVerifyId: null,
    customSingle1: null,
    customSingle2: null,
    isLoading: false,
    isSaving: false,
    isUploading: false,
    errors: {},
    visibility: {},
    validations: {},
    labels: {},
    projectIdMap: null,
    customText1: '',
    customText2: '',
    eventDate: null,
};

export const getters: GetterTree<GrievanceFormState, RootState> = {
    getFieldErrors: state => (field: string) => state.errors[field],
    getAllFieldErrors: state => () => state.errors,
    isFormValid: state => _.isEmpty(state.errors),
    getFieldLabel: state => (field: string) => state.labels[field],
    isFieldVisible: state => (field: string) => {
        if (!state.visibility[field]) {
            return false;
        }
        return state.visibility[field].visible && state.visibility[field].enabled;
    },
    isFieldRequired: state => (field: string) => _.some(state.validations[field], val => val.type === 'required'),
    getFieldMaxLength: state => (field: string) => {
        const validation = _.find(state.validations[field], val => val.type === 'maxlength');
        return validation?.argument;
    },
};

export const mutations: MutationTree<GrievanceFormState> = {
    setDescription(state, payload) {
        state.description = payload.description.trim();
        state.errors = clearErrors(state.errors, 'description');
    },
    setType(state, payload) {
        state.type = payload.type;
        state.errors = clearErrors(state.errors, 'type');
    },
    setParentCategory(state, payload) {
        state.parentCategory = payload.parentCategory;
        state.errors = clearErrors(state.errors, 'parentCategory');
    },
    setCategory(state, payload) {
        state.category = payload.category;
        state.errors = clearErrors(state.errors, 'category');
    },
    setDocuments(state, payload) {
        state.documents = payload.documents;
    },
    setAnonymous(state, payload) {
        state.anonymous = payload.anonymous;
        state.errors = clearErrors(state.errors, 'firstName', 'lastName');
        if (!payload.showAnonymousEmailNotification) {
            state.notification = false;
        }
    },
    setTitle(state, payload) {
        state.title = payload.title;
    },
    setFirstName(state, payload) {
        state.firstName = payload.firstName.trim();
        state.errors = clearErrors(state.errors, 'firstName');
    },
    setLastName(state, payload) {
        state.lastName = payload.lastName.trim();
        state.errors = clearErrors(state.errors, 'lastName');
    },
    setNotification(state, payload) {
        state.notification = payload.notification;
        state.errors = clearErrors(state.errors, 'email');
    },
    setEmail(state, payload) {
        state.email = payload.email.trim();
        state.errors = clearErrors(state.errors, 'email');
    },
    setPhone(state, payload) {
        state.phone = payload.phone.trim();
    },
    setAgeGroup(state, payload) {
        state.ageGroup = payload.ageGroup;
    },
    setEthnicGroup(state, payload) {
        state.ethnicGroup = payload.ethnicGroup;
    },
    setAddressField(state, payload: { name: keyof GrievanceFormState; value: any }) {
        (state as any)[payload.name] = payload.value;
        state.errors = clearErrors(state.errors, payload.name);
    },
    setFirstTimeUsed(state, payload) {
        state.firstTimeUsed = payload.firstTimeUsed;
    },
    setReferer(state, payload) {
        state.referer = payload.referer;
    },
    setAcceptTerms(state, payload) {
        state.acceptTerms = payload.acceptTerms;
        state.errors = clearErrors(state.errors, 'acceptTerms');
    },
    setCustomTextarea1Toggle(state, payload) {
        state.customTextarea1Toggle = payload.customTextarea1Toggle;
    },
    setCustomTextarea1(state, payload) {
        state.customTextarea1 = payload.customTextarea1;
        state.errors = clearErrors(state.errors, 'customTextarea1');
    },
    setCustomTextarea2(state, payload) {
        state.customTextarea2 = payload.customTextarea2;
        state.errors = clearErrors(state.errors, 'customTextarea2');
    },
    setDepartment(state, payload) {
        state.department = payload.department;
        state.errors = clearErrors(state.errors, 'department');
    },
    setCustomSingle1(state, payload) {
        state.customSingle1 = payload.customSingle1;
        state.errors = clearErrors(state.errors, 'customSingle1');
    },
    setCustomSingle2(state, payload) {
        state.customSingle2 = payload.customSingle2;
        state.errors = clearErrors(state.errors, 'customSingle2');
    },
    setRecaptchaVerifyId(state, payload) {
        state.recaptchaVerifyId = payload.recaptchaVerifyId;
        state.errors = clearErrors(state.errors);
    },
    setIsLoading(state, payload) {
        state.isLoading = payload.isLoading;
        if (state.isLoading) {
            NProgress.start();
        } else {
            NProgress.done();
        }
    },
    setIsSaving(state, payload) {
        state.isSaving = payload.isSaving;
        if (state.isSaving) {
            NProgress.start();
        } else {
            NProgress.done();
        }
    },
    setIsUploading(state, payload) {
        state.isUploading = payload.isUploading;
    },
    setErrors(state, payload) {
        state.errors = payload.errors;
    },
    setVisibility(state, payload) {
        state.visibility = payload.visibility;
    },
    setValidations(state, payload) {
        state.validations = payload.validations;
    },
    setLabels(state, payload) {
        state.labels = payload.labels;
    },
    setProjectIdMap(state, payload) {
        state.projectIdMap = payload.projectIdMap;
        state.errors = clearErrors(state.errors, 'projectIdMap');
    },
    setCustomText1(state, payload) {
        state.customText1 = payload.customText1;
        state.errors = clearErrors(state.errors, 'customText1');
    },
    setCustomText2(state, payload) {
        state.customText2 = payload.customText2;
        state.errors = clearErrors(state.errors, 'customText2');
    },
    setEventDate(state, payload) {
        state.eventDate = payload.eventDate;
        state.errors = clearErrors(state.errors, 'eventDate');
    },
    resetForm(state) {
        state.type = null;
        state.parentCategory = null;
        state.category = null;
        state.description = '';
        state.documents = [];
        state.anonymous = false;
        state.title = null;
        state.firstName = '';
        state.lastName = '';
        state.notification = false;
        state.email = '';
        state.phone = '';
        state.addressLine1 = '';
        state.addressLine2 = '';
        state.neighborhood = '';
        state.sublocality = '';
        state.city = '';
        state.state = '';
        state.country = '';
        state.postalCode = '';
        state.sublocality2 = '';
        state.arealevel2 = '';
        state.ageGroup = null;
        state.ethnicGroup = null;
        state.firstTimeUsed = true;
        state.referer = null;
        state.acceptTerms = false;
        state.recaptchaVerifyId = null;
        state.department = null;
        state.customSingle1 = null;
        state.customSingle2 = null;
        state.customTextarea1Toggle = false;
        state.customTextarea1 = '';
        state.customTextarea2 = '';
        state.isLoading = false;
        state.isSaving = false;
        state.isUploading = false;
        state.errors = {};
        state.projectIdMap = null;
        state.customText1 = '';
        state.customText2 = '';
        state.eventDate = null;
    },
};

export const actions: ActionTree<GrievanceFormState, RootState> = {
    async initForm({ commit, rootState }) {
        commit('setIsLoading', { isLoading: true });
        try {
            const response = await axios.get(`${rootState.instanceId}/portal_data/grievances/add`);
            commit('setValidations', { validations: response.data.metadata.validations });
            commit('setVisibility', { visibility: response.data.metadata.fields });
            commit('setLabels', { labels: response.data.metadata.labels });
            const data = response.data.data;

            if (data) {
                commit('setDescription', { description: data.description?.value ?? '' });
                commit('setType', { type: data.type?.value ?? null });
                commit('setParentCategory', { parentCategory: data.parentCategory?.value ?? null });
                commit('setCategory', { category: data.category?.value ?? null });
                commit('setAnonymous', { anonymous: data.anonymous?.value ?? false });
                commit('setTitle', { title: data.title?.value ?? null });
                commit('setFirstName', { firstName: data.firstName?.value ?? '' });
                commit('setLastName', { lastName: data.lastName?.value ?? '' });
                commit('setNotification', { notification: data.notification?.value ?? false });
                commit('setEmail', { email: data.email?.value ?? '' });
                commit('setPhone', { phone: data.phone?.value ?? '' });
                commit('setAgeGroup', { ageGroup: data.ageGroup?.value ?? null });
                commit('setEthnicGroup', { ethnicGroup: data.ethnicGroup?.value ?? null });

                commit('setAddressField', { name: 'state', value: data.state?.value ?? '' });
                commit('setAddressField', { name: 'country', value: data.country?.value ?? '' });
                commit('setAddressField', { name: 'city', value: data.city?.value ?? '' });
                commit('setAddressField', { name: 'neighborhood', value: data.neighborhood?.value ?? '' });
                commit('setAddressField', { name: 'sublocality', value: data.sublocality?.value ?? '' });
                commit('setAddressField', { name: 'sublocality2', value: data.sublocality2?.value ?? '' });
                commit('setAddressField', { name: 'arealevel2', value: data.arealevel2?.value ?? '' });
                commit('setAddressField', { name: 'postalCode', value: data.postalCode?.value ?? '' });

                commit('setFirstTimeUsed', { firstTimeUsed: data.firstTimeUsed?.value ?? true });
                commit('setReferer', { referer: data.referer?.value ?? null });
                commit('setCustomTextarea1Toggle', {
                    customTextarea1Toggle: data.setCustomTextarea1Toggle?.value ?? false,
                });
                commit('setCustomTextarea1', { customTextarea1: data.customTextarea1?.value ?? '' });
                commit('setCustomTextarea2', { customTextarea2: data.customTextarea2?.value ?? '' });
                commit('setDepartment', { department: data.department?.value ?? null });
                commit('setCustomSingle1', { customSingle1: data.customSingle1?.value ?? null });
                commit('setCustomSingle2', { customSingle2: data.customSingle2?.value ?? null });
                commit('setCustomText1', { customText1: data.customText1?.value ?? '' });
                commit('setCustomText2', { customText2: data.customText2?.value ?? '' });
                commit('setEventDate', { eventDate: data.eventDate?.value ?? null });
            }
        } finally {
            commit('setIsLoading', { isLoading: false });
        }
    },
    async saveGrievance({ state, rootState, commit, getters }) {
        const errors = await validateSchema(createGrievanceValidationSchema(state, getters), {
            type: state.type,
            parentCategory: state.parentCategory,
            category: state.category,
            description: state.description,
            firstName: state.firstName,
            lastName: state.lastName,
            email: state.email,
            addressLine1: state.addressLine1,
            addressLine2: state.addressLine2,
            neighborhood: state.neighborhood,
            sublocality: state.sublocality,
            city: state.city,
            state: state.state,
            country: state.country,
            postalCode: state.postalCode,
            sublocality2: state.sublocality2,
            arealevel2: state.arealevel2,
            anonymous: state.anonymous,
            notification: state.notification,
            acceptTerms: state.acceptTerms,
            customTextarea1: state.customTextarea1,
            customTextarea2: state.customTextarea2,
            customSingle1: state.customSingle1,
            customSingle2: state.customSingle2,
            customText1: state.customText1,
            customText2: state.customText2,
            eventDate: state.eventDate,
            projectIdMap: state.projectIdMap,
        });
        if (errors) {
            return commit('setErrors', { errors });
        }
        commit('setIsSaving', { isSaving: true });
        let payload: CreateGrievancePayload = {
            type: { value: state.type?.value ?? null },
            parentCategory: { value: state.parentCategory?.value ?? null },
            category: { value: state.category?.value ?? null },
            description: { value: state.description },
            firstTimeUsed: { value: state.firstTimeUsed },
            referer: { value: state.referer?.value ?? null },
            anonymous: { value: state.anonymous },
            notification: { value: state.notification },
            email: { value: state.email },
        };
        if (!state.anonymous) {
            payload.title = { value: state.title?.value ?? null };
            payload.firstName = { value: state.firstName };
            payload.lastName = { value: state.lastName };
            payload.ageGroup = { value: state.ageGroup?.value ?? null };
            payload.ethnicGroup = { value: state.ethnicGroup?.value ?? null };
            payload.phone = { value: state.phone };
            payload.addressLine1 = { value: state.addressLine1 };
            payload.addressLine2 = { value: state.addressLine2 };
            payload.neighborhood = { value: state.neighborhood };
            payload.sublocality = { value: state.sublocality };
            payload.city = { value: state.city };
            payload.state = { value: state.state };
            payload.country = { value: state.country };
            payload.postalCode = { value: state.postalCode };
            payload.sublocality2 = { value: state.sublocality2 };
            payload.arealevel2 = { value: state.arealevel2 };
        }
        if (state.customTextarea1Toggle) {
            payload.customTextarea1Toggle = { value: state.customTextarea1Toggle };
            payload.customTextarea1 = { value: state.customTextarea1 };
        }
        if (state.customTextarea2) {
            payload.customTextarea2 = { value: state.customTextarea2 };
        }
        if (state.department?.value) {
            payload.department = { value: state.department.value };
        }
        if (state.customSingle1?.value) {
            payload.customSingle1 = { value: state.customSingle1.value };
        }
        if (state.customSingle2?.value) {
            payload.customSingle2 = { value: state.customSingle2.value };
        }
        if (state.projectIdMap?.value) {
            payload.projectIdMap = { value: state.projectIdMap.value };
        }
        if (state.customText1) {
            payload.customText1 = { value: state.customText1 };
        }
        if (state.customText2) {
            payload.customText2 = { value: state.customText2 };
        }
        if (state.eventDate) {
            payload.eventDate = { value: state.eventDate };
        }
        payload = cleanPayload(payload, getters.isFieldVisible);

        const nonErroredDocuments = state.documents?.filter(d => !d.error).map(d => d.id);
        if (nonErroredDocuments?.length) {
            payload.sysDocuments = { value: nonErroredDocuments };
        }
        payload.recaptchaVerifyId = state.recaptchaVerifyId ?? '';

        try {
            const response = await axios.post(`${rootState.instanceId}/portal_data/grievances/add`, payload);
            if (response.data?.success !== true) {
                const message = response.data?.message ?? 'An error occured. Please try again.';
                commit('setErrors', { errors: { global: [message] } });
                return;
            }
            commit(
                'confirm/init',
                { refNo: response.data.refNo, passwordCreationToken: response.data.passwordCreationToken },
                { root: true },
            );
        } catch (err) {
            const message = err.response?.data?.message ?? 'An error occured. Please try again.';
            commit('setErrors', { errors: { global: [message] } });
        } finally {
            commit('setIsSaving', { isSaving: false });
        }
    },
};

const GrievanceFormStore: Module<GrievanceFormState, RootState> = {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};

export default GrievanceFormStore;
